import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';

import yup from '../../../utility/yup';
import { Scenes } from '../../../types/items';
import {
  ItemFormReact,
  ReactFormInput,
  TabsForm,
  ScriptEditor,
  ScriptReadOnly,
  FormInputHidden,
} from '../../../components';
import { useActionDispatcher } from '../../../hooks';
import {
  saveScenesRequest,
  deleteScenesRequest,
} from '../../../store/modules/scenes/actions';
import DeleteAction from '../../../components/action/DeleteAction';
import CopyScriptTextAction from './actions/CopyScriptTextAction';
import useStyles from './styles';
import AdvancedActionCell from '../../../components/action-cell/AdvancedActionCell';
import CopyScriptTextAndInstructionsAction from './actions/CopyScriptTextAndInstructionAction';
import DuplicateScriptAction from './actions/DuplicateScriptAction';
import ScriptWordCounter from './ScriptWordCounter';

type Props = {
  creatingMode?: boolean;
  scene: Scenes;
};

const sceneSchema = yup.object().shape({
  title: yup.string().required().trim(),
});

const ScriptForm = ({
  creatingMode = false,
  scene,
}: Props) => {
  const classes = useStyles();
  const { id: sceneId, permissions: { can_edit: canEdit, can_delete: canDelete } } = scene;

  const script: string = useMemo(() => {
    return scene.script || '';
  }, [scene]);

  const deleteScenes = useActionDispatcher(deleteScenesRequest);
  const saveScene = useActionDispatcher(saveScenesRequest);

  return (
    <ItemFormReact
      creatingMode={creatingMode}
      initialValues={scene}
      validationSchema={sceneSchema}
      onSubmit={saveScene}
      loading={false}
      editPermitted={Boolean(canEdit)}
      key={sceneId}
      hideActions
    >
      <Grid container alignItems="center">
        <Grid item xs={12} style={{ marginBottom: 20 }}>
          <ReactFormInput
            submitOnBlur
            label=""
            name="title"
            classes={{ input: classes.title, inputUnderline: classes.inputUnderline }}
            disabled={!canEdit}
            endAdornment={(
              <div className={classes.additionalPanel}>
                <ScriptWordCounter script={script} />
                <div>
                  <AdvancedActionCell>
                    {canEdit && <DuplicateScriptAction />}
                    <CopyScriptTextAction script={script} />
                    <CopyScriptTextAndInstructionsAction script={script} />
                    {canDelete && <DeleteAction original={scene} onDelete={deleteScenes} />}
                  </AdvancedActionCell>
                </div>
              </div>
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TabsForm
            tabs={[
              {
                title: 'Edit',
                content: (<ScriptEditor canEdit={canEdit} name="script" />),
              },
              {
                title: 'Preview',
                content: (<ScriptReadOnly name="script" />),
              },
            ]}
          />
        </Grid>
      </Grid>
      <FormInputHidden name="script" />
    </ItemFormReact>
  );
};

export default ScriptForm;
